// Единая точка аналитики приложения: старт счётчиков, цели, события продукта и пользователь.
// Каждая цель уходит сразу в три системы: Метрика (прежнее имя цели), Amplitude
// (имя в стиле «Signed Up») и dataLayer GTM → GA4 (рекомендованные имена sign_up/login/purchase).

import type { User } from '@shared/types';
import { withAmplitude, initAmplitude } from './amplitude';
import { gtmPush, initGtm } from './gtm';
import { initMetrika, ymGoal } from './metrika';

type Goal = 'registration' | 'login' | 'oauth_login' | 'purchase' | 'donation';

const GOAL_EVENTS: Record<Goal, { amplitude: string; ga4: string; props?: Record<string, unknown> }> = {
  registration: { amplitude: 'Signed Up', ga4: 'sign_up', props: { method: 'email' } },
  login: { amplitude: 'Logged In', ga4: 'login', props: { method: 'email' } },
  oauth_login: { amplitude: 'Logged In', ga4: 'login', props: { method: 'yandex' } },
  purchase: { amplitude: 'Purchase Completed', ga4: 'purchase' },
  donation: { amplitude: 'Donation Completed', ga4: 'donation' },
};

/** Однократный старт всех счётчиков (только в production-сборке). */
export function initAnalytics(): void {
  initMetrika();
  initGtm();
  initAmplitude();
}

/**
 * Достижение цели (регистрация, вход, оплата). params.amount — сумма в рублях,
 * params.transaction_id — id платежа: по нему Amplitude отбрасывает повтор покупки (insert_id).
 * В dataLayer transaction_id тоже уходит, но GA4 получит его, только когда тег GTM
 * «GA4 — цели Lomora» начнёт передавать этот параметр (шаг владельца, docs/project/operations.md).
 * До этого в GA4 и Метрике повтор отсекает лишь отметка lx.analytics.paid.<pid> (PaymentSuccessPage).
 */
export function trackGoal(goal: Goal, params?: Record<string, unknown>): void {
  const { amplitude, ga4, props } = GOAL_EVENTS[goal];
  const all = { ...props, ...params };
  const amount = typeof all.amount === 'number' ? all.amount : undefined;
  const transactionId = typeof all.transaction_id === 'string' ? all.transaction_id : undefined;
  // Доход цели Метрика берёт из order_price и currency.
  ymGoal(goal, amount === undefined ? params : { ...params, order_price: amount, currency: 'RUB' });
  withAmplitude((a) => {
    // Событие с уже отправленным insert_id Amplitude второй раз не засчитывает.
    a.track(amplitude, all, transactionId ? { insert_id: `${goal}_${transactionId}` } : undefined);
    if (amount === undefined) return;
    // Выручка — отдельным событием revenue(): из него строятся отчёты Revenue и LTV.
    const revenue = new a.Revenue()
      .setProductId(typeof all.tier === 'string' && all.tier ? all.tier : goal)
      .setPrice(amount)
      .setQuantity(1)
      .setRevenueType(goal)
      .setCurrency('RUB');
    a.revenue(revenue, transactionId ? { insert_id: `${goal}_${transactionId}_revenue` } : undefined);
  });
  gtmPush({
    event: ga4,
    ...all,
    ...(amount !== undefined ? { value: amount, currency: 'RUB' } : {}),
  });
}

/**
 * Событие продукта (не цель): Amplitude и dataLayer GTM. В Метрику новые события не уходят — там
 * только цели trackGoal. В GA4 событие попадёт, только если владелец расширит регулярку триггера
 * «GA4 — цели Lomora» в GTM (notes.md, раздел «Аналитика»). Введённый текст и ПДн в props не класть.
 */
export function trackEvent(name: string, props?: Record<string, unknown>): void {
  withAmplitude((a) => a.track(name, props));
  gtmPush({ ...props, event: name });
}

/** Пользователь вошёл (или восстановлен из хранилища при старте). */
export function identifyUser(user: User): void {
  withAmplitude((a) => {
    a.setUserId(user.id);
    const props = new a.Identify().set('email_verified', user.emailVerified);
    if (user.lang) props.set('lang', user.lang);
    if (user.level) props.set('level', user.level);
    a.identify(props);
  });
  gtmPush({ event: 'user_identified', user_id: user.id });
}

/**
 * Пользователь вышел. Только при выходе, не при старте гостем: reset() выдаёт новый
 * device_id и рвёт связь с визитами лендинга.
 */
export function resetUser(): void {
  withAmplitude((a) => a.reset());
  gtmPush({ event: 'user_signed_out', user_id: undefined });
}

/** Синхронизация пользователя аналитики со стором авторизации. */
export function syncAnalyticsUser(prev: User | null, next: User | null): void {
  if (next && next.id !== prev?.id) identifyUser(next);
  else if (!next && prev) resetUser();
}
